import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Chip, ChipProps, Tooltip, alpha } from "@mui/material";
import { GroupsOutlined, PersonOutline } from "@mui/icons-material";
import color from "../../../Constants/colors";
import {
  DocumentCraftingLabel,
  DocumentCraftingLabelColor,
  DocumentCraftingLabelScope,
} from "../../../Models/documentCrafting";

/** The 8 fixed label colours (the backend stores only the name). */
export const LABEL_COLOR_HEX: Record<DocumentCraftingLabelColor, string> = {
  navy: "#1e3a5f",
  blue: "#2a78d6",
  cyan: "#0891b2",
  teal: "#0f766e",
  green: "#15803d",
  amber: "#d97706",
  rose: "#e11d48",
  grey: "#6b7280",
};

export const labelColorHex = (value: string) =>
  LABEL_COLOR_HEX[value as DocumentCraftingLabelColor] ?? LABEL_COLOR_HEX.grey;

/** Group icon for project labels, person icon for personal ones. */
export const LabelScopeIcon: React.FC<{
  scope: DocumentCraftingLabelScope;
  size?: number;
}> = ({ scope, size = 14 }) => {
  const Icon =
    scope === DocumentCraftingLabelScope.PERSONAL ? PersonOutline : GroupsOutlined;
  return <Icon sx={{ fontSize: size, color: color.gray500, flexShrink: 0 }} />;
};

type LabelChipProps = Omit<ChipProps, "label" | "color"> & {
  label: DocumentCraftingLabel;
};

/**
 * One label: colour dot, scope icon and name. Archived project labels are
 * greyed. Accepts the props of Autocomplete's `getTagProps` (and `onDelete`).
 */
export const LabelChip: React.FC<LabelChipProps> = ({
  label,
  sx,
  ...chipProps
}) => {
  const { t } = useTranslation("documentCrafting");
  const hex = labelColorHex(label.color);
  const scopeText =
    label.scope === DocumentCraftingLabelScope.PERSONAL
      ? t("common.labels.personal")
      : t("common.labels.project");
  const title = label.archived
    ? `${scopeText} · ${t("common.labels.archived")}`
    : scopeText;

  return (
    <Tooltip title={title} arrow>
      <Chip
        size="small"
        {...chipProps}
        label={
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center", gap: 0.6 }}
          >
            <Box
              component="span"
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                flexShrink: 0,
                backgroundColor: label.archived ? color.gray400 : hex,
              }}
            />
            <LabelScopeIcon scope={label.scope} size={13} />
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                textDecoration: label.archived ? "line-through" : "none",
              }}
            >
              {label.name}
            </Box>
          </Box>
        }
        sx={[
          {
            height: 24,
            maxWidth: 220,
            borderRadius: "12px",
            fontWeight: 600,
            color: label.archived ? color.gray500 : color.gray900,
            backgroundColor: label.archived ? color.gray100 : alpha(hex, 0.1),
            border: `1px solid ${label.archived ? color.gray200 : alpha(hex, 0.35)}`,
            "& .MuiChip-label": { px: 1, fontSize: 12 },
            "& .MuiChip-deleteIcon": {
              color: color.gray400,
              fontSize: 16,
              "&:hover": { color: color.gray700 },
            },
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      />
    </Tooltip>
  );
};

/** A small swatch of a label colour, e.g. in a colour picker. */
export const LabelColorDot: React.FC<{
  value: DocumentCraftingLabelColor;
  size?: number;
}> = ({ value, size = 12 }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      width: size,
      height: size,
      borderRadius: "50%",
      flexShrink: 0,
      backgroundColor: LABEL_COLOR_HEX[value],
    }}
  />
);

/** Project labels first, then the user's personal labels; `max` folds the
 * rest into "+N" (names in the tooltip). */
const LabelChips: React.FC<{
  projectLabels?: DocumentCraftingLabel[];
  personalLabels?: DocumentCraftingLabel[];
  max?: number;
}> = ({ projectLabels = [], personalLabels = [], max }) => {
  const labels = [...projectLabels, ...personalLabels];
  if (!labels.length) return null;
  const shown = max !== undefined ? labels.slice(0, max) : labels;
  const hidden = labels.slice(shown.length);

  return (
    <Box
      sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}
    >
      {shown.map((label) => (
        <LabelChip key={`${label.scope}-${label.id}`} label={label} />
      ))}
      {hidden.length > 0 && (
        <Tooltip
          title={hidden.map((label) => label.name).join(", ")}
          arrow
        >
          <Chip
            size="small"
            label={`+${hidden.length}`}
            sx={{
              height: 24,
              borderRadius: "12px",
              fontWeight: 700,
              color: color.gray700,
              backgroundColor: color.gray100,
              "& .MuiChip-label": { px: 1, fontSize: 12 },
            }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export default LabelChips;
